import { NgClass } from '@angular/common';
import { Component, input, InputSignal, output, OutputEmitterRef } from '@angular/core';
import { MatButtonToggle, MatButtonToggleChange, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { ISegmentedButtonOption, TSegmentedButtonSize } from './segmented-button.interface';

/**
 * Single-select segmented control: a light framed pill whose active segment is a soft tonal fill,
 * instead of the solid navy `mat-button-toggle` the global theme paints (`scw-mat-theme.scss`).
 *
 * It wraps `mat-button-toggle-group` so the group's roving-focus keyboard navigation and ARIA
 * semantics come for free; only the presentation is overridden.
 *
 * The control is *stateless*: `value` is the single source of truth and is never written to
 * internally, so the parent stays free to reject, transform or persist a selection before it shows
 * up (see `ItemsViewControlsComponent`, where item scope round-trips through the filter card).
 */
@Component({
  imports: [MatButtonToggle, MatButtonToggleGroup, MatIcon, MatTooltip, NgClass, TranslateModule],
  selector: 'app-segmented-button',
  standalone: true,
  styleUrl: './segmented-button.component.scss',
  templateUrl: './segmented-button.component.html',
})
export class SegmentedButtonComponent<TValue = string> {
  private static instanceCount = 0;

  /** Ties the visible caption to the group via `aria-labelledby`, so both stay in sync. */
  public readonly captionId = `segmented-button-caption-${SegmentedButtonComponent.instanceCount++}`;

  public readonly options: InputSignal<ISegmentedButtonOption<TValue>[]> =
    input.required<ISegmentedButtonOption<TValue>[]>();

  /** Currently selected segment. A value matching no option leaves the control with nothing selected. */
  public readonly value: InputSignal<TValue> = input.required<TValue>();

  /** Optional visible caption rendered before the control. Ignored when `captionKey` is set. */
  public readonly caption: InputSignal<string> = input<string>('');

  /** i18n key for the visible caption. */
  public readonly captionKey: InputSignal<string> = input<string>('');

  /** Labels the group for screen readers. Redundant once a caption is shown — the caption labels it. */
  public readonly ariaLabel: InputSignal<string> = input<string>('');

  public readonly size: InputSignal<TSegmentedButtonSize> = input<TSegmentedButtonSize>('medium');

  public readonly disabled: InputSignal<boolean> = input<boolean>(false);

  /** Emitted only on user interaction — never when `value` changes from the outside. */
  public readonly valueChange: OutputEmitterRef<TValue> = output<TValue>();

  public onChange(change: MatButtonToggleChange): void {
    this.valueChange.emit(change.value as TValue);
  }
}
